import {
	ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, output, signal,
	ElementRef, viewChild,
} from '@angular/core';
import { forkJoin } from 'rxjs';
import cytoscape from 'cytoscape';
// @ts-expect-error — no types for cytoscape-dagre
import cytoscapeDagre from 'cytoscape-dagre';
import { EditorDataService } from '../../../features/editor/services/editor-data.service';
import { ThemeService } from '../../../core/services/theme.service';
import { ChapterData, ProjectData, SceneRef, TimelineSceneBlock } from '../../models';
import { buildCytoscapeElements, GraphNodeKind, GraphNodeRef } from './utils/graph-builder';

interface SceneJsonData {
	framesCount?: number;
}

type GraphViewMode = 'scenes' | 'chapters';

cytoscape.use(cytoscapeDagre);

@Component({
	selector: 'app-graph-panel',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [],
	templateUrl: './graph-panel.html',
	styleUrl: './graph-panel.scss',
})
export class GraphPanelComponent {
	// ── Inputs/outputs ──────────────────────────────────────────────────────
	projectData = input<ProjectData>();
	slug = input.required<string>();
	activeSceneSlug = input<string | null>(null);
	argsMap = input<Record<string, string | number | boolean>>({});

	sceneSelect = output<{ chapterSlug: string | null; sceneSlug: string }>();
	chapterSelect = output<{ chapterSlug: string }>();

	// ── Internal state ──────────────────────────────────────────────────────
	selectedChapterIndex = input(0);
	protected scenesLoading = signal(false);
	protected hasGraph = signal(false);
	protected viewMode = signal<GraphViewMode>('chapters');

	private allBlocks = signal<TimelineSceneBlock[]>([]);
	private currentSceneRefs = signal<SceneRef[]>([]);
	private dataService = inject(EditorDataService);
	private themeService = inject(ThemeService);
	private previousRenderKey = '';
	private cy: cytoscape.Core | null = null;

	protected cyContainer = viewChild<ElementRef<HTMLDivElement>>('cyContainer');

	// ── Computed ─────────────────────────────────────────────────────────────
	protected chapters = computed(() => {
		const data = this.projectData();
		return data?.chapters ?? [];
	});

	protected hasChapters = computed(() => this.chapters().length > 0);

	protected chapterOptions = computed(() =>
		this.chapters().map((ch, i) => ({ label: ch.slug, value: i }))
	);

	protected selectedChapter = computed(() => {
		const chs = this.chapters();
		const idx = this.selectedChapterIndex();
		return chs[idx] ?? null;
	});

	private activeChapterSlug = computed(() => this.selectedChapter()?.slug ?? null);

	protected fps = computed(() => this.projectData()?.fps ?? 25);

	// ── Constructor ─────────────────────────────────────────────────────────
	private resizeObserver: ResizeObserver | null = null;

	constructor() {
		const destroyRef = inject(DestroyRef);
		destroyRef.onDestroy(() => {
			this.cy?.destroy();
			this.cy = null;
			this.resizeObserver?.disconnect();
		});

		// Auto-fit when container becomes visible/resizes
		effect(() => {
			const container = this.cyContainer()?.nativeElement;
			if (!container) return;

			this.resizeObserver?.disconnect();
			this.resizeObserver = new ResizeObserver(() => {
				if (this.cy && container.clientWidth > 0 && container.clientHeight > 0) {
					this.cy.resize();
					this.fitGraph();
				}
			});
			this.resizeObserver.observe(container);
		});

		// Render the graph based on the current view mode + project data.
		effect(() => {
			const slug = this.slug();
			const fps = this.fps();
			const data = this.projectData();
			const mode = this.viewMode();
			if (!slug || !data) {
				this.clearGraph();
				return;
			}

			if (mode === 'chapters' && this.hasChapters()) {
				this.renderChaptersView(this.chapters());
				return;
			}
			// Fall through to scenes-view when project has no chapters, regardless of mode.

			this.renderScenesView(slug, data, fps);
		});

		// Update highlights when active scene/chapter or argsMap changes (no re-layout)
		effect(() => {
			const active = this.activeSceneSlug();
			const activeChapter = this.activeChapterSlug();
			const args = this.argsMap();
			this.updateHighlights(active, activeChapter, args);
		});

		// Update theme when dark mode changes
		effect(() => {
			const isDark = this.themeService.isDark();
			if (this.cy) {
				this.cy.style(this.getCyStyle(isDark) as unknown as cytoscape.StylesheetCSS[]);
			}
		});
	}


	// ── Protected (template) ────────────────────────────────────────────────
	protected fitGraph(): void {
		if (!this.cy) return;
		const container = this.cyContainer()?.nativeElement;
		// Use smaller padding when the container is short (e.g. bottom panel)
		const padding = container && container.clientHeight < 200 ? 10 : 40;
		this.cy.fit(undefined, padding);
	}

	// ── Private ─────────────────────────────────────────────────────────────
	private clearGraph(): void {
		this.allBlocks.set([]);
		this.currentSceneRefs.set([]);
		this.hasGraph.set(false);
		this.previousRenderKey = '';
		if (this.cy) {
			this.cy.destroy();
			this.cy = null;
		}
	}

	private renderScenesView(slug: string, data: ProjectData, fps: number): void {
		const chapter = this.selectedChapter();
		const hasChapters = this.hasChapters();

		if (hasChapters && !chapter) {
			this.clearGraph();
			return;
		}

		// Paired with the RAW index for the same reason the timeline does it: a
		// block must always be able to name the ref it came from, even here where
		// nothing writes back yet. See `timeline-panel/ref-index.ts`.
		const sceneRefs: { ref: SceneRef; rawIndex: number }[] = (hasChapters ? (chapter?.scenes ?? []) : (data.scenes ?? []))
			.map((ref, rawIndex) => ({ ref, rawIndex }))
			.filter(({ ref }) => !ref.alt);
		const chapterKey = hasChapters ? (chapter?.slug ?? '') : '__project__';
		const sceneKeys = sceneRefs.map(({ ref }) => ref.slug + (ref.externalChapter ?? '')).join(',');
		const key = `scenes:${chapterKey}:${sceneKeys}`;
		if (key === this.previousRenderKey) return;
		this.previousRenderKey = key;

		this.currentSceneRefs.set(sceneRefs.map(({ ref }) => ref));
		this.fetchSceneBlocks(slug, hasChapters ? (chapter ?? null) : null, sceneRefs, fps);
	}

	private renderChaptersView(chapters: ChapterData[]): void {
		const key = `chapters:${chapters.map(c => c.slug).join(',')}`;
		if (key === this.previousRenderKey) return;
		this.previousRenderKey = key;

		this.scenesLoading.set(false);
		this.currentSceneRefs.set([]);
		this.allBlocks.set([]);

		const refs: GraphNodeRef[] = chapters.map(ch => ({
			slug: ch.slug,
			disabled: ch.disabled,
			attributes: ch.attributes,
			scenesCount: ch.scenes?.length ?? 0,
		}));

		this.rebuildGraph(refs, [], this.activeChapterSlug(), this.argsMap(), 'chapter');
	}

	private rebuildGraph(
		refs: GraphNodeRef[],
		blocks: TimelineSceneBlock[],
		active: string | null,
		argsMap: Record<string, string | number | boolean>,
		kind: GraphNodeKind,
	): void {
		const elements = buildCytoscapeElements(refs, blocks, active, argsMap, kind);
		if (!elements.length) {
			this.hasGraph.set(false);
			if (this.cy) {
				this.cy.destroy();
				this.cy = null;
			}
			return;
		}

		const container = this.cyContainer()?.nativeElement;
		if (!container) {
			requestAnimationFrame(() => this.rebuildGraph(refs, blocks, active, argsMap, kind));
			return;
		}

		this.hasGraph.set(true);
		const isDark = this.themeService.isDark();

		if (this.cy) {
			this.cy.destroy();
		}

		this.cy = cytoscape({
			container,
			elements: elements as unknown as cytoscape.ElementDefinition[],
			style: this.getCyStyle(isDark) as unknown as cytoscape.StylesheetCSS[],
			layout: {
				name: 'dagre',
				rankDir: 'LR',
				nodeSep: 30,
				rankSep: 100,
				edgeSep: 20,
			} as unknown as cytoscape.LayoutOptions,
			userZoomingEnabled: true,
			userPanningEnabled: true,
			boxSelectionEnabled: false,
			autoungrabify: true,
			minZoom: 0.1,
		});

		// Scene node click: select scene inside the current chapter.
		this.cy.on('tap', 'node.scene', (evt) => {
			const sceneSlug = evt.target.data('slug');
			if (!sceneSlug) return;
			const chapter = this.selectedChapter();
			this.sceneSelect.emit({
				chapterSlug: chapter?.slug ?? null,
				sceneSlug,
			});
		});

		// Chapter node click: drill down — select the chapter and flip to scenes-view.
		this.cy.on('tap', 'node.chapter', (evt) => {
			const chapterSlug = evt.target.data('slug');
			if (!chapterSlug) return;
			this.chapterSelect.emit({ chapterSlug });
			this.viewMode.set('scenes');
		});

		// Fit after layout
		this.cy.on('layoutstop', () => {
			if (!this.cy) return;
			const h = container.clientHeight;
			this.cy.fit(undefined, h < 200 ? 10 : 40);
		});
	}

	/** Check if a composite attrValue (e.g. "GENDER=male&ZELENKA_VARIANT=loss") matches argsMap */
	private matchesCompositeAttr(attrValue: string, argsMap: Record<string, string | number | boolean>): { allSelected: boolean; matches: boolean } {
		// attrValue can be a simple value (old single-attr) or composite "KEY=val&KEY2=val2"
		if (!attrValue.includes('=')) {
			// Simple single-attribute value — legacy format, shouldn't happen with new builder
			return { allSelected: false, matches: false };
		}
		const pairs = attrValue.split('&');
		let allSelected = true;
		let allMatch = true;
		for (const pair of pairs) {
			const eqIdx = pair.indexOf('=');
			const key = pair.substring(0, eqIdx);
			const value = pair.substring(eqIdx + 1);
			if (!(key in argsMap) || argsMap[key] == null || String(argsMap[key]) === 'undefined') {
				allSelected = false;
				allMatch = false;
			} else if (String(argsMap[key]) !== value) {
				allMatch = false;
			}
		}
		return { allSelected, matches: allMatch };
	}

	private updateHighlights(
		activeScene: string | null,
		activeChapter: string | null,
		argsMap: Record<string, string | number | boolean>,
	): void {
		const cy = this.cy;
		if (!cy) return;

		const hasAnySelection = Object.keys(argsMap).some(
			k => argsMap[k] != null && String(argsMap[k]) !== 'undefined'
		);

		cy.batch(() => {
			cy.nodes('.scene').forEach(node => {
				const slug = node.data('slug');
				const attrValue = node.data('attrValue');

				node.toggleClass('active', slug === activeScene);

				if (attrValue) {
					const { allSelected, matches } = this.matchesCompositeAttr(attrValue, argsMap);
					node.toggleClass('on-path', allSelected && matches);
					node.toggleClass('off-path', allSelected && !matches);
				} else {
					node.toggleClass('on-path', hasAnySelection);
					node.toggleClass('off-path', false);
				}
			});

			cy.nodes('.chapter').forEach(node => {
				const slug = node.data('slug');
				const attrValue = node.data('attrValue');

				node.toggleClass('active', slug === activeChapter);

				if (attrValue) {
					const { allSelected, matches } = this.matchesCompositeAttr(attrValue, argsMap);
					node.toggleClass('on-path', allSelected && matches);
					node.toggleClass('off-path', allSelected && !matches);
				} else {
					node.toggleClass('on-path', hasAnySelection);
					node.toggleClass('off-path', false);
				}
			});

			cy.nodes('.fork').forEach(node => {
				const attrKey = node.data('attrKey');
				if (attrKey) {
					const selectedValue = argsMap[attrKey] != null ? String(argsMap[attrKey]) : null;
					const selValid = selectedValue != null && selectedValue !== 'undefined';
					node.toggleClass('on-path', selValid);
				}
			});

			cy.edges().forEach(edge => {
				const attrValue = edge.data('attrValue');

				if (attrValue) {
					const { allSelected, matches } = this.matchesCompositeAttr(attrValue, argsMap);
					edge.toggleClass('on-path', allSelected && matches);
					edge.toggleClass('off-path', allSelected && !matches);
				} else {
					edge.toggleClass('on-path', hasAnySelection);
					edge.toggleClass('off-path', false);
				}
			});
		});
	}

	private getCyStyle(isDark: boolean): Record<string, unknown>[] {
		const bg = isDark ? '#0a0e1a' : '#f1f5f9';
		const textColor = isDark ? '#e2e8f0' : '#1e293b';

		// Scenes — indigo palette
		const sceneBg = isDark ? 'rgba(99, 102, 241, 0.25)' : 'rgba(99, 102, 241, 0.15)';
		const sceneBorder = isDark ? 'rgba(99, 102, 241, 0.6)' : 'rgba(99, 102, 241, 0.5)';
		const sceneActiveBg = isDark ? 'rgba(99, 102, 241, 0.5)' : 'rgba(99, 102, 241, 0.35)';
		const sceneActiveBorder = isDark ? '#818cf8' : '#6366f1';

		// Chapters — muted teal palette (distinct "zoom out" feel)
		const chapterBg = isDark ? 'rgba(45, 212, 191, 0.2)' : 'rgba(45, 212, 191, 0.15)';
		const chapterBorder = isDark ? 'rgba(45, 212, 191, 0.6)' : 'rgba(20, 184, 166, 0.55)';
		const chapterActiveBg = isDark ? 'rgba(45, 212, 191, 0.45)' : 'rgba(45, 212, 191, 0.35)';
		const chapterActiveBorder = isDark ? '#2dd4bf' : '#14b8a6';

		const forkBg = isDark ? 'rgba(245, 158, 11, 0.25)' : 'rgba(245, 158, 11, 0.15)';
		const forkBorder = isDark ? 'rgba(245, 158, 11, 0.7)' : 'rgba(245, 158, 11, 0.5)';
		const edgeColor = isDark ? '#475569' : '#94a3b8';
		const edgeLabelColor = isDark ? '#94a3b8' : '#64748b';

		// On-path highlight colors (green/emerald)
		const onPathBorder = isDark ? '#34d399' : '#10b981';
		const onPathEdge = isDark ? '#34d399' : '#10b981';

		return [
			{
				selector: 'node.scene',
				style: {
					'shape': 'round-rectangle',
					'width': 160,
					'height': 60,
					'background-color': sceneBg,
					'border-width': 2,
					'border-color': sceneBorder,
					'label': 'data(label)',
					'text-wrap': 'wrap',
					'text-valign': 'center',
					'text-halign': 'center',
					'font-size': 11,
					'color': textColor,
					'text-max-width': '140px',
				},
			},
			{
				selector: 'node.scene.active',
				style: {
					'background-color': sceneActiveBg,
					'border-color': sceneActiveBorder,
					'border-width': 3,
				},
			},
			{
				selector: 'node.scene.disabled',
				style: { 'opacity': 0.35 },
			},
			{
				selector: 'node.scene.on-path',
				style: {
					'border-color': onPathBorder,
					'border-width': 3,
				},
			},
			{
				selector: 'node.scene.off-path',
				style: { 'opacity': 0.3 },
			},
			{
				selector: 'node.chapter',
				style: {
					'shape': 'round-rectangle',
					'width': 170,
					'height': 64,
					'background-color': chapterBg,
					'border-width': 2,
					'border-color': chapterBorder,
					'label': 'data(label)',
					'text-wrap': 'wrap',
					'text-valign': 'center',
					'text-halign': 'center',
					'font-size': 11,
					'font-weight': 'bold',
					'color': textColor,
					'text-max-width': '150px',
				},
			},
			{
				selector: 'node.chapter.active',
				style: {
					'background-color': chapterActiveBg,
					'border-color': chapterActiveBorder,
					'border-width': 3,
				},
			},
			{
				selector: 'node.chapter.disabled',
				style: { 'opacity': 0.35 },
			},
			{
				selector: 'node.chapter.on-path',
				style: {
					'border-color': onPathBorder,
					'border-width': 3,
				},
			},
			{
				selector: 'node.chapter.off-path',
				style: { 'opacity': 0.3 },
			},
			{
				selector: 'node.fork',
				style: {
					'shape': 'round-rectangle',
					'width': 160,
					'height': 60,
					'background-color': forkBg,
					'border-width': 2,
					'border-color': forkBorder,
					'label': 'data(label)',
					'text-wrap': 'wrap',
					'text-valign': 'center',
					'text-halign': 'center',
					'font-size': 12,
					'font-weight': 'bold',
					'color': textColor,
					'text-transform': 'uppercase',
					'text-max-width': '140px',
				},
			},
			{
				selector: 'node.fork.on-path',
				style: {
					'border-color': onPathBorder,
					'border-width': 3,
				},
			},
			{
				selector: 'edge',
				style: {
					'width': 2,
					'line-color': edgeColor,
					'target-arrow-color': edgeColor,
					'target-arrow-shape': 'triangle',
					'curve-style': 'taxi',
					'taxi-direction': 'rightward',
					'taxi-turn': '50%',
					'arrow-scale': 0.8,
					'label': 'data(label)',
					'font-size': 10,
					'color': edgeLabelColor,
					'text-background-color': bg,
					'text-background-opacity': 0.8,
					'text-background-padding': '2px',
					'text-rotation': 'autorotate',
				},
			},
			{
				selector: 'edge.on-path',
				style: {
					'width': 3,
					'line-color': onPathEdge,
					'target-arrow-color': onPathEdge,
					'arrow-scale': 1,
				},
			},
			{
				selector: 'edge.off-path',
				style: { 'opacity': 0.25 },
			},
		];
	}

	private fetchSceneBlocks(projectSlug: string, chapter: ChapterData | null, sceneRefs: { ref: SceneRef; rawIndex: number }[], fps: number): void {
		if (!sceneRefs.length) {
			this.clearGraph();
			return;
		}

		this.scenesLoading.set(true);

		const chapterScenePath = (ch: string, sc: string) =>
			`/projects/${projectSlug}/chapters/${ch}/scenes/${sc}/scene.json`;
		const projectScenePath = (sc: string) =>
			`/projects/${projectSlug}/scenes/${sc}/scene.json`;

		const requests = sceneRefs.map(({ ref }) => {
			let scenePath: (sc: string) => string;

			if (ref.externalChapter) {
				const slash = (ref.externalChapter as string).indexOf('/');
				const chSlug = ref.externalChapter.substring(0, slash);
				scenePath = (sc) => chapterScenePath(chSlug, sc);
			} else if (chapter) {
				scenePath = (sc) => chapterScenePath(chapter.slug, sc);
			} else {
				scenePath = projectScenePath;
			}

			const scSlug = ref.externalChapter
				? ref.externalChapter.substring(ref.externalChapter.indexOf('/') + 1)
				: ref.slug;

			const splitCount = ref.split && ref.split > 1 ? ref.split : 1;
			if (splitCount > 1) {
				const splitRequests = Array.from({ length: splitCount }, (_, i) =>
					this.dataService.loadFile<SceneJsonData>(scenePath(`${scSlug}__${i}`))
				);
				return forkJoin(splitRequests);
			}
			return this.dataService.loadFile<SceneJsonData>(scenePath(scSlug));
		});

		forkJoin(requests).subscribe({
			next: (results) => {
				const blocks: TimelineSceneBlock[] = [];
				let cumulative = 0;
				sceneRefs.forEach(({ ref, rawIndex }, i) => {
					const result = results[i];
					if (Array.isArray(result)) {
						const totalFrames = result.reduce((sum, part) => sum + (part.framesCount ?? 0), 0);
						const duration = totalFrames / fps;
						blocks.push({
							refIndex: rawIndex,
							slug: ref.slug,
							framesCount: totalFrames,
							durationSeconds: duration,
							startSeconds: cumulative,
							disabled: ref.disabled,
							externalChapter: ref.externalChapter,
							attributes: ref.attributes,
						});
						cumulative += duration;
					} else {
						const framesCount = (result as SceneJsonData).framesCount ?? 0;
						const duration = framesCount / fps;
						blocks.push({
							refIndex: rawIndex,
							slug: ref.slug,
							framesCount,
							durationSeconds: duration,
							startSeconds: cumulative,
							disabled: ref.disabled,
							externalChapter: ref.externalChapter,
							attributes: ref.attributes,
						});
						cumulative += duration;
					}
				});
				this.allBlocks.set(blocks);
				this.currentSceneRefs.set(sceneRefs.map(({ ref }) => ref));
				this.scenesLoading.set(false);

				const active = this.activeSceneSlug();
				const args = this.argsMap();
				this.rebuildGraph(sceneRefs.map(({ ref }) => ref) as GraphNodeRef[], blocks, active, args, 'scene');
			},
			error: () => {
				this.allBlocks.set([]);
				this.scenesLoading.set(false);
				this.hasGraph.set(false);
			},
		});
	}
}
